import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { applyTailwindChangeToClasses } from "./applyChanges";
import { PropertyType } from "@/tailwind/tailwindUtils";
import { areClassesUnchanged, calculateNewClasses } from ".";

export const getUpdateClassChangeForElement = ({
  codebaseId,
  newWidth,
  newHeight,
  oldWidth,
  oldHeight,
  newLeft,
  newTop,
  oldLeft,
  oldTop,
}: {
  codebaseId: string;
  newWidth?: number;
  newHeight?: number;
  oldWidth?: number;
  oldHeight?: number;
  newLeft?: number;
  newTop?: number;
  oldLeft?: number;
  oldTop?: number;
}): UpdateClassesChange | undefined => {
  const treeElementLookup = useCodebaseStore.getState().treeElementLookup;
  const treeElement = treeElementLookup[codebaseId];
  if (!treeElement) {
    return;
  }

  const tailwindClassesMap = useCodebaseStore.getState().tailwindClassesMap;

  // Width
  let currentElementClasses = treeElement.removableClasses ?? [];
  const allInvolvedClasses: any[] = [];
  if (newWidth !== undefined && oldWidth !== newWidth) {
    const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
      currentElementClasses,
      option: {
        tailwind: `w-[${newWidth}px]`,
        css: `width: ${newWidth}px`,
      },
      propertyType: PropertyType.WIDTH,
    });

    currentElementClasses = newClasses;
    allInvolvedClasses.push(...involvedClasses);
  }

  // Height
  if (newHeight !== undefined && oldHeight !== newHeight) {
    const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
      currentElementClasses,
      option: {
        tailwind: `h-[${newHeight}px]`,
        css: `height: ${newHeight}px`,
      },
      propertyType: PropertyType.HEIGHT,
    });

    currentElementClasses = newClasses;
    allInvolvedClasses.push(...involvedClasses);
  }

  // Left
  if (newLeft !== undefined && oldLeft !== newLeft) {
    const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
      currentElementClasses,
      option: {
        tailwind: `left-[${newLeft}px]`,
        css: `left: ${newLeft}px`,
      },
      propertyType: PropertyType.LEFT,
    });

    currentElementClasses = newClasses;
    allInvolvedClasses.push(...involvedClasses);

    const newClassesAfterClear = calculateNewClasses({
      option: null,
      propertyType: PropertyType.RIGHT,
      oldClasses: currentElementClasses,
      tailwindClassesMap,
    });
    currentElementClasses = newClassesAfterClear;
  }

  // Top
  if (newTop !== undefined && oldTop !== newTop) {
    const { newClasses: newClasses2, involvedClasses: involvedClasses2 } =
      applyTailwindChangeToClasses({
        currentElementClasses,
        option: {
          tailwind: `top-[${newTop}px]`,
          css: `top: ${newTop}px`,
        },
        propertyType: PropertyType.TOP,
      });

    currentElementClasses = newClasses2;
    allInvolvedClasses.push(...involvedClasses2);

    const newClassesAfterClear2 = calculateNewClasses({
      option: null,
      propertyType: PropertyType.BOTTOM,
      oldClasses: currentElementClasses,
      tailwindClassesMap,
    });

    currentElementClasses = newClassesAfterClear2;
  }

  if (
    areClassesUnchanged(
      currentElementClasses,
      treeElement.removableClasses ?? [],
    )
  ) {
    return;
  }

  return new UpdateClassesChange({
    codebaseIdToUpdateClass: codebaseId,
    newClasses: currentElementClasses,
    oldClasses: treeElement.removableClasses ?? [],
    involvedClasses: allInvolvedClasses,
  });
};
